package fr.arcplex.network.impl.coherence

import fr.arcplex.network.AuthProvider
import fr.arcplex.network.NetworkConfig
import fr.arcplex.network.RendererKind
import fr.arcplex.network.SoonerApi
import fr.arcplex.network.model.Branding
import fr.arcplex.network.model.Film
import fr.arcplex.network.model.PlaybackSource
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.model.Screen
import fr.arcplex.network.model.ScreenAction
import fr.arcplex.network.model.ScreenKind
import fr.arcplex.network.model.SearchPage

/**
 * Coherence backend decorator: film lookups go through the new API and fall
 * back to the legacy film page when the Coherence response is unusable.
 * Every other port concern is delegated (branding is fetched here, playback and
 * navigation go straight to the delegate).
 */
class CoherenceSoonerApi(
    private val delegate: SoonerApi,
    private val config: NetworkConfig
) : SoonerApi {

    private val filmApi = CoherenceFilmApi(config)
    private val themeApi = CoherenceThemeApi(config)

    override val renderer: RendererKind = RendererKind.COHERENCE

    override suspend fun fetchRoot(): Result<Screen> = delegate.fetchRoot()

    override suspend fun fetchScreen(actionUrl: String): Result<Screen> = delegate.fetchScreen(actionUrl)

    override suspend fun fetchFilm(filmId: String): Result<Film> {
        val legacy = delegate.fetchFilm(filmId).getOrNull()
        val info = filmApi.getFilmInfo(filmId).getOrNull()

        val film = if (info != null) {
            Film(
                id = filmId,
                screen = legacy?.screen ?: emptyLegacyScreen(filmId),
                coherence = info
            )
        } else {
            legacy ?: return Result.failure(IllegalStateException("no film data for $filmId"))
        }
        return Result.success(film)
    }

    override suspend fun search(actionUrl: String, query: String, validate: Boolean): Result<SearchPage> =
        delegate.search(actionUrl, query, validate)

    override suspend fun resolvePlayback(item: RailItem): Result<PlaybackSource> =
        delegate.resolvePlayback(item)

    override suspend fun cachedBranding(): Branding = themeApi.cached()

    override suspend fun refreshBranding(): Result<Branding> = themeApi.refreshBranding()

    override fun resolveAction(item: RailItem, currentScreen: Screen?): ScreenAction =
        delegate.resolveAction(item, currentScreen)

    private fun emptyLegacyScreen(filmId: String): Screen =
        Screen(
            id = filmId,
            kind = ScreenKind.FILM,
            label = "",
            backgroundImage = null,
            backgroundHex = "#0A0E17",
            menu = Rail(id = "", uid = "", type = "", height = 60f, focusable = true),
            hero = Rail(id = "", uid = "", type = "", height = 60f, focusable = true),
            rails = emptyList()
        )
}
